import { Deferred } from "@lib/utils/deferred";

import { PitchShiftNode } from "./pitch-shift";
import { TrackInfo } from "./types";
import { Track } from "./Track";

/**
 * Region data passed into a playback backend.
 *
 * Legacy playback requires `audioBuffer`, while the streaming backend can run
 * from encoded bytes plus region metadata alone.
 */
export interface LoadedRegion {
  id: string;
  trackId: string;
  audioBuffer?: AudioBuffer;
  audioBlob?: Blob;
  duration?: number;
  position: number;
  codec: string | null;
  preDecodedPcm?: Float32Array;
}

interface Options {
  audioContext: AudioContext;
  sampleRate: 48000;
  tracks: TrackInfo[];
  regions: LoadedRegion[];
}

type ReadyStateListener = (ready: Promise<void>) => void;

function requireDecodedRegionAudio(region: LoadedRegion): AudioBuffer {
  if (!region.audioBuffer) {
    throw new Error(`Legacy playback requires decoded audio for region ${region.id}`);
  }

  return region.audioBuffer;
}

export class AudioPlayer {
  audioContext: AudioContext;

  private readyDeferred: Deferred<void> = new Deferred();
  private tracks: Track[] = [];
  private state: "uninitialized" | "initializing" | "initialized" | "destroyed" = "uninitialized";
  private masterGainNode: GainNode;
  private pitchShiftNode: PitchShiftNode | null = null;
  private readyStateListeners: Set<ReadyStateListener> = new Set();
  private playbackRate: number = 1.0;
  private transpose: number = 0;
  private pitchShiftBypassed: boolean = true;

  async load({ audioContext, tracks, regions, sampleRate: _ }: Options) {
    if (this.state === "destroyed") return;
    if (this.state !== "uninitialized") {
      throw new Error("load() can only be called once");
    }
    this.state = "initializing";

    this.audioContext = audioContext;

    this.masterGainNode = this.audioContext.createGain();

    // Create pitch shift node and connect its output to destination
    this.pitchShiftNode = await PitchShiftNode.create(this.audioContext);
    this.pitchShiftNode.node.connect(this.audioContext.destination);

    // Start bypassed — route master gain directly to destination.
    // updatePitchShift() will insert the pitch shift node when needed.
    this.pitchShiftBypassed = true;
    this.masterGainNode.connect(this.audioContext.destination);

    // Create tracks
    tracks.forEach((trackInfo) => {
      const track = new Track({
        id: trackInfo.id,
        audioContext: this.audioContext,
        destination: this.masterGainNode,
      });

      this.tracks.push(track);
    });

    // Add regions to their tracks
    regions.forEach((region) => {
      const track = this.tracks.find(c => c.id === region.trackId);
      if (track) {
        track.addRegion({
          id: region.id,
          audioBuffer: requireDecodedRegionAudio(region),
          position: region.position,
        });
      }
    });

    this.state = "initialized";
    this.readyDeferred.resolve();
  }

  get ready() {
    return this.readyDeferred.promise;
  }

  /**
   * Subscribe to ready state changes.
   * The listener will be called whenever the ready promise is updated.
   * @param listener Function to call with the new ready promise
   * @returns Function to unsubscribe
   */
  onReadyStateChange(listener: ReadyStateListener): () => void {
    this.readyStateListeners.add(listener);
    return () => {
      this.readyStateListeners.delete(listener);
    };
  }

  /**
   * Notify all listeners that the ready state has changed.
   * @private
   */
  private notifyReadyStateChange(): void {
    this.readyStateListeners.forEach(listener => {
      listener(this.readyDeferred.promise);
    });
  }

  /**
   * Signal a ready state change and immediately resolve.
   * Since regions now always receive pre-decoded AudioBuffers,
   * readiness is instant — but we still notify listeners so the
   * engine can track the state transition.
   * @private
   */
  private updateReady(): void {
    const newDeferred = new Deferred<void>();
    this.readyDeferred = newDeferred;

    // Notify listeners that ready state is changing
    this.notifyReadyStateChange();

    // Resolve immediately since sources are always ready
    newDeferred.resolve();
  }

  addTracks({ tracks, regions }: { tracks: TrackInfo[], regions: LoadedRegion[] }) {
    // NOTE: Ensure that playback is stopped before adding a track
    tracks.forEach((trackInfo) => {
      const track = new Track({
        id: trackInfo.id,
        audioContext: this.audioContext,
        destination: this.masterGainNode,
      });

      this.tracks.push(track);
    });

    // Add regions to their tracks
    regions.forEach((region) => {
      const track = this.tracks.find(c => c.id === region.trackId);
      if (track) {
        track.addRegion({
          id: region.id,
          audioBuffer: requireDecodedRegionAudio(region),
          position: region.position,
        });
      }
    });

    // Notify listeners of ready state change
    this.updateReady();
  }

  /**
   * Add an empty track (no regions yet).
   * Used for creating tracks before recording into them.
   */
  addEmptyTrack(id: string) {
    // NOTE: Ensure that playback is stopped before adding a track
    const track = new Track({
      id,
      audioContext: this.audioContext,
      destination: this.masterGainNode,
    });

    this.tracks.push(track);
  }

  removeTrack(id: string) {
    const track = this.tracks.find((c) => c.id === id);
    if (track) {
      track.destroy()
      this.tracks.splice(this.tracks.indexOf(track), 1);
    }
  }

  /**
   * Get a track by ID.
   */
  getTrack(id: string): Track | undefined {
    return this.tracks.find((c) => c.id === id);
  }

  /**
   * Add a region to an existing track.
   * Used when uploading audio to an existing track or adding recorded audio.
   * Receives a pre-decoded AudioBuffer.
   */
  addRegionToTrack(trackId: string, region: LoadedRegion) {
    const track = this.tracks.find((c) => c.id === trackId);
    if (!track) throw new Error(`Track ${trackId} not found`);

    track.addRegion({
      id: region.id,
      audioBuffer: requireDecodedRegionAudio(region),
      position: region.position,
    });

    // Notify listeners of ready state change
    this.updateReady();
  }

  play(timestamp: number) {
    if (this.state !== "initialized") throw new Error("Not yet loaded");

    if (this.audioContext.state === "suspended") this.audioContext.resume();
    this.tracks.forEach((c) => c.play(timestamp, this.playbackRate));
  }

  pause() {
    if (this.state !== "initialized") throw new Error("Not yet loaded");

    this.tracks.forEach((c) => c.pause());
  }

  async seek(timestamp: number) {
    if (this.state !== "initialized") throw new Error("Not yet loaded");

    await Promise.all(
      this.tracks.map(async (c) => await c.seek(timestamp)),
    );
  }

  setMasterGain(gain: number) {
    this.masterGainNode.gain.value = gain;
  }

  /**
   * Set the playback rate for all tracks.
   * Also adjusts the pitch shift node to correct for pitch change.
   *
   * @param rate - Playback rate (e.g., 0.5, 1.0, 1.5, 2.0)
   */
  setPlaybackRate(rate: number) {
    this.playbackRate = rate;
    this.updatePitchShift();
  }

  /**
   * Set the transpose offset in semitones.
   * Positive shifts pitch up, negative shifts down.
   *
   * @param semitones - Transpose offset (e.g., -12 to +12)
   */
  setTranspose(semitones: number) {
    this.transpose = semitones;
    this.updatePitchShift();
  }

  /**
   * Update the pitch shift node with combined rate correction and transpose.
   * Rate correction: -12 * log2(rate) semitones (cancels pitch from speed change)
   * Transpose: additional semitone offset from user
   *
   * When the total shift is effectively zero (1x speed, no transpose),
   * bypasses the pitch shift node entirely to save CPU.
   */
  private updatePitchShift() {
    if (!this.pitchShiftNode) return;
    const rateCorrection = -12 * Math.log2(this.playbackRate);
    const totalSemitones = rateCorrection + this.transpose;
    const shouldBypass = Math.abs(totalSemitones) < 0.01;

    if (shouldBypass && !this.pitchShiftBypassed) {
      // Bypass: route master gain directly to destination, disconnect from pitch shift
      this.masterGainNode.disconnect(this.pitchShiftNode.node);
      this.masterGainNode.connect(this.audioContext.destination);
      this.pitchShiftBypassed = true;
    } else if (!shouldBypass && this.pitchShiftBypassed) {
      // Engage: route master gain through pitch shift node
      this.masterGainNode.disconnect(this.audioContext.destination);
      this.masterGainNode.connect(this.pitchShiftNode.node);
      this.pitchShiftBypassed = false;
    }

    if (!shouldBypass) {
      this.pitchShiftNode.setSemitones(totalSemitones);
    }
  }

  setTrackGain(trackId: string, gain: number) {
    const track = this.tracks.find((c) => c.id === trackId);
    if (!track) throw new Error(`Track ${trackId} not found`);

    track.setGain(gain);
  }

  destroy() {
    // Stop any current playback
    if (this.state === "initialized") {
      this.pause();
    }

    // Destroy all tracks and clean up their resources
    this.tracks.forEach((track) => {
      track.destroy();
    });

    // Disconnect the master gain node and pitch shift node
    if (this.masterGainNode) {
      this.masterGainNode.disconnect();
    }
    if (this.pitchShiftNode) {
      this.pitchShiftNode.destroy();
      this.pitchShiftNode = null;
    }

    // Clear the tracks array
    this.tracks = [];

    // Reset state
    this.state = "destroyed";
  }
}
